import {
	isSendableEmailStatus,
	toEmailStatus,
	unhandledEmailStatus,
} from "@signalist/shared-types";

import { EmailIconProps } from "./EmailIcon";

/**
 * Maps a prospect/offer/mapBusiness `emailStatus` (+ stored address) to the {@link EmailIcon}
 * badge. Bounced states map to their own badges so a bounced address is visually distinct from a
 * never-tried "failed" find:
 * - a sendable status (`VALID`, `RISKY`: catch-all or unknown, PREC-4265) with an address → `checked`
 * - `BOUNCED_VALID`   → `bounced-valid`   (amber — bounced but still verifies; maybe temporary)
 * - `BOUNCED_INVALID` → `bounced-invalid` (red — bounced and no longer verifies; treat as dead)
 * - `RETRY`           → `retry`           (amber — the provider could not answer; try again)
 *
 * The switch is exhaustive over the shared `EmailStatus` union, so a new member fails to compile
 * here instead of silently inheriting the `refresh` default (PREC-5035).
 */
export const getEmailIconStatus = (
	email?: string | null,
	status?: string | null
): EmailIconProps["status"] => {
	if (email && isSendableEmailStatus(status)) {
		return "checked";
	}

	const emailStatus = toEmailStatus(status);

	switch (emailStatus) {
		case "BOUNCED_VALID":
			return "bounced-valid";
		case "BOUNCED_INVALID":
			return "bounced-invalid";
		case "INVALID":
			return "failed";
		case "RETRY":
			return "retry";
		case "SEARCHING":
			return "searching";
		case "VALID":
		case "RISKY":
		case null:
			return "refresh";
		default:
			return unhandledEmailStatus(emailStatus);
	}
};
